import {FocusKeys} from '@primer/behaviors'
import {useFocusZone} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef, type ReactNode} from 'react'

import styles from './ChoiceButton.module.css'

export interface ChoiceButtonProps {
  /** Number displayed in the leading badge. */
  number: number
  /** Primary label. */
  label: ReactNode
  /** Optional secondary description. */
  description?: ReactNode
  /** Shows a visual multi-select checkbox. */
  showCheckbox?: boolean
  /** Whether the visual checkbox is selected. */
  checked?: boolean
  /** Whether this is the recommended choice. */
  isRecommended?: boolean
  /** Whether the choice can be activated. */
  disabled?: boolean
  /** Called when the choice is activated. */
  onClick?: () => void
  /** Custom row content rendered instead of the label and description. */
  children?: ReactNode
  /** Interactive content rendered at the end of the row. */
  optionsSlot?: ReactNode
  /** Accessible name override for the choice. */
  ariaLabel?: string
}

export interface ChoiceButtonInputProps {
  /** Number displayed in the leading badge. */
  number: number
  /** Accessible name for the input. */
  ariaLabel?: string
  /** Placeholder text for the input. */
  placeholder?: string
  /** Current input value. */
  value: string
  /** Called when the input value changes. */
  onChange: (value: string) => void
  /** Called when Enter submits the input. */
  onSubmit?: () => void
  /** Whether the input can be edited. */
  disabled?: boolean
  /** Whether the input receives focus on mount. */
  autoFocus?: boolean
}

export interface ChoiceButtonGroupProps {
  /** Choice rows in the group. */
  children: ReactNode
  /** Whether the first enabled choice receives focus on mount. */
  autoFocus?: boolean
  /** Accessible name for the choice group. */
  'aria-label'?: string
}

/** Numbered choice row for interactive prompts. */
export function ChoiceButton({
  number,
  label,
  description,
  showCheckbox = false,
  checked = false,
  isRecommended = false,
  disabled = false,
  onClick,
  children,
  optionsSlot,
  ariaLabel,
}: ChoiceButtonProps) {
  const handleClick = () => {
    if (!disabled) onClick?.()
  }
  const content = (
    <>
      <NumberBadge value={number} />
      {showCheckbox ? (
        <span className={clsx(styles.Checkbox, checked && styles.checkboxChecked)} aria-hidden="true" />
      ) : null}
      <div className={styles.Text}>
        {children ?? (
          <>
            <div className={styles.Title}>{label}</div>
            {description ? <div className={styles.Description}>{description}</div> : null}
          </>
        )}
      </div>
    </>
  )

  if (optionsSlot) {
    return (
      <div className={clsx(styles.Row, styles.rowWithSlot, isRecommended && styles.recommended)}>
        <button
          type="button"
          data-choice-button
          className={styles.OverlayButton}
          onClick={handleClick}
          aria-disabled={disabled}
          aria-label={ariaLabel}
          aria-pressed={showCheckbox ? checked : undefined}
        >
          <span className="sr-only">{typeof label === 'string' ? label : 'Select'}</span>
        </button>
        <span className={styles.RowContent}>{content}</span>
        <span className={styles.OptionsSlot}>{optionsSlot}</span>
        {isRecommended ? <KeybindingHint /> : null}
      </div>
    )
  }

  return (
    <button
      type="button"
      data-choice-button
      className={clsx(styles.Row, isRecommended && styles.recommended)}
      onClick={handleClick}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={showCheckbox ? checked : undefined}
    >
      {content}
      {isRecommended ? <KeybindingHint /> : null}
    </button>
  )
}

/** Text-input row for a freeform choice. */
export function ChoiceButtonInput({
  number,
  ariaLabel,
  placeholder,
  value,
  onChange,
  onSubmit,
  disabled = false,
  autoFocus = true,
}: ChoiceButtonInputProps) {
  return (
    <div className={clsx(styles.Row, styles.inputRow)}>
      <NumberBadge value={number} />
      <input
        type="text"
        className={styles.Input}
        aria-label={ariaLabel ?? placeholder}
        aria-disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={event => {
          if (!disabled) onChange(event.target.value)
        }}
        onKeyDown={event => {
          if (disabled) return
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (event.key === 'Enter' && !event.nativeEvent.isComposing && onSubmit) {
            event.preventDefault()
            onSubmit()
          }
        }}
        readOnly={disabled}
        autoFocus={autoFocus}
      />
    </div>
  )
}

/** Focus-managed vertical group of choice rows. */
export function ChoiceButtonGroup({
  children,
  autoFocus = true,
  'aria-label': ariaLabel = 'Choices',
}: ChoiceButtonGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useFocusZone({
    containerRef,
    focusInStrategy: 'first',
    focusOutBehavior: 'wrap',
    bindKeys: FocusKeys.ArrowVertical | FocusKeys.HomeAndEnd,
    focusableElementFilter: element =>
      element.hasAttribute('data-choice-button') && element.getAttribute('aria-disabled') !== 'true',
  })

  useEffect(() => {
    if (!autoFocus) return
    const firstChoice = containerRef.current?.querySelector<HTMLElement>(
      '[data-choice-button]:not([aria-disabled="true"])',
    )
    firstChoice?.focus()
  }, [autoFocus])

  return (
    <div ref={containerRef} className={styles.Group} role="toolbar" aria-orientation="vertical" aria-label={ariaLabel}>
      {children}
    </div>
  )
}

function NumberBadge({value}: {value: number}) {
  return <span className={styles.NumberBadge}>{value}</span>
}

function KeybindingHint() {
  return (
    <span className={styles.KeybindingHint} aria-hidden="true">
      <kbd className={styles.Kbd}>↵</kbd>
    </span>
  )
}
